import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import type { SocialLink } from '../../api/types'
import * as Pk from '../ui/icons/pack'

interface Props {
  links?: SocialLink[] | null
  className?: string
  iconSize?: number
  stopPropagation?: boolean
  /** Глифы из пака Paper (редизайн 2026-09): globe / X. Экраны, ещё не
   *  перенесённые на макет, остаются на брендовых глифах. Telegram is not
   *  among them: it is one glyph on every surface (TelegramGlyph). */
  pack?: boolean
}

// No Telegram here, on purpose (operator 2026-10-12: "the Telegram icon must be
// one and the same everywhere; why is Discover's a different, worse one"). The
// pack's outlined plane was what Discover and the token page drew.
const PACK_GLYPHS: Record<string, typeof Pk.Web> = {
  website: Pk.Web,
  x: Pk.X,
}

/** The Telegram mark — the one every surface draws: a Trenches card, a
 *  Discover row, the token page, Search, the phone screens, the filter pills,
 *  the login buttons. The brand plane alone, no circular backing; a tight
 *  viewBox so the plane fills the same box as the other social marks. It takes
 *  its colour from the element it stands in (currentColor). */
export function TelegramGlyph({ size = 12 }: { size?: number }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="2.6 4 18.8 15.2" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9.78 18.65 10.06 14.42 17.74 7.5c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3L18.24 18.65c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42Z"
      />
    </svg>
  )
}

const ICONS: Record<string, { label: string; path: string }> = {
  x: {
    label: 'X (Twitter)',
    path: 'M214.75,211.71l-62.6-98.38,61.77-67.95a8,8,0,0,0-11.84-10.76L143.24,99.34,102.75,35.71A8,8,0,0,0,96,32H48a8,8,0,0,0-6.75,12.3l62.6,98.37-61.77,68a8,8,0,1,0,11.84,10.76l58.84-64.72,40.49,63.63A8,8,0,0,0,160,224h48a8,8,0,0,0,6.75-12.29ZM164.39,208,62.57,48h29L193.43,208Z',
  },
  telegram: {
    label: 'Telegram',
    path: '',
  },
  website: {
    label: 'Website',
    path: 'M128,24h0A104,104,0,1,0,232,128,104.12,104.12,0,0,0,128,24Zm88,104a87.61,87.61,0,0,1-3.33,24H174.16a157.44,157.44,0,0,0,0-48h38.51A87.61,87.61,0,0,1,216,128ZM102,168H154a115.11,115.11,0,0,1-26,45A115.27,115.27,0,0,1,102,168Zm-3.9-16a140.84,140.84,0,0,1,0-48h59.88a140.84,140.84,0,0,1,0,48ZM40,128a87.61,87.61,0,0,1,3.33-24H81.84a157.44,157.44,0,0,0,0,48H43.33A87.61,87.61,0,0,1,40,128ZM154,88H102a115.11,115.11,0,0,1,26-45A115.27,115.27,0,0,1,154,88Zm52.33,0H170.71a135.28,135.28,0,0,0-22.3-45.6A88.29,88.29,0,0,1,206.37,88ZM107.59,42.4A135.28,135.28,0,0,0,85.29,88H49.63A88.29,88.29,0,0,1,107.59,42.4ZM49.63,168H85.29a135.28,135.28,0,0,0,22.3,45.6A88.29,88.29,0,0,1,49.63,168Zm98.78,45.6a135.28,135.28,0,0,0,22.3-45.6h35.66A88.29,88.29,0,0,1,148.41,213.6Z',
  },
}

/** The bare social mark, for filter pills and other non-link chrome. Same
 *  glyphs as the links below — the one place a social is ever drawn from
 *  (конституция §2: соцсети — SocialLinks). 'any' is the generic link mark. */
export function SocialGlyph({ type, size = 12 }: { type: string; size?: number }): ReactNode {
  if (type === 'telegram') return <TelegramGlyph size={size} />
  if (type === 'any') {
    return (
      <svg width={size} height={size} viewBox="0 0 256 256" aria-hidden="true">
        <path
          d="M137.54,186.36a8,8,0,0,1,0,11.31l-9.94,10A56,56,0,0,1,48.38,128.4L72.5,104.28A56,56,0,0,1,149.31,102a8,8,0,1,1-10.64,12,40,40,0,0,0-54.85,1.63L59.7,139.72a40,40,0,0,0,56.58,56.58l9.94-9.94A8,8,0,0,1,137.54,186.36Zm70.08-138a56.08,56.08,0,0,0-79.22,0l-9.94,9.95a8,8,0,0,0,11.32,11.31l9.94-9.94a40,40,0,0,1,56.58,56.58L172.18,140.4A40,40,0,0,1,117.33,142,8,8,0,1,0,106.69,154a56,56,0,0,0,76.81-2.26l24.12-24.12A56.08,56.08,0,0,0,207.62,48.38Z"
          fill="currentColor"
        />
      </svg>
    )
  }
  const icon = ICONS[type]
  if (!icon || !icon.path) return null
  return (
    <svg width={size} height={size} viewBox="0 0 256 256" aria-hidden="true">
      <path d={icon.path} fill="currentColor" />
    </svg>
  )
}

const VISUALLY_HIDDEN_STYLE: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

export function SocialLinks({
  links,
  className,
  iconSize = 16,
  stopPropagation = true,
  pack = false,
}: Props): ReactNode {
  const visible = (links ?? []).filter((link) => ICONS[link.type] && link.url)
  if (visible.length === 0) return null

  return (
    <>
      {visible.map((link) => {
        const icon = ICONS[link.type]
        if (!icon) return null
        const PackGlyph = pack ? PACK_GLYPHS[link.type] : undefined
        return (
          <a
            key={`${link.type}:${link.url}`}
            className={className}
            data-type={link.type}
            aria-label={icon.label}
            href={link.url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              if (stopPropagation) e.stopPropagation()
            }}
          >
            <span style={VISUALLY_HIDDEN_STYLE}>{icon.label}</span>
            {PackGlyph ? (
              <PackGlyph size={iconSize} />
            ) : (
              <SocialGlyph type={link.type} size={iconSize} />
            )}
          </a>
        )
      })}
    </>
  )
}
